import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, IconButton, Grid, MenuItem, Select, InputLabel, FormControl, Chip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { LocalizationProvider, DatePicker, TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import frLocale from 'date-fns/locale/fr';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import { useParams } from 'react-router-dom';
import { userMock } from './PanelAdmin';

const MAX_POINTS = 4;
const sportsList = ['Football', 'Basketball', 'Volleyball', 'Tennis', 'Martial Arts'];

const AdminEventConfig = () => {
    const { id } = useParams();
    // Simulated data (same structure as EventDetail.js)
    const eventData = {
        1: {
            id: 1,
            title: 'Football Match - Liga 1',
            description: 'Alianza Lima vs Universitario de Deportes - The Peruvian football classic. A historic match between the two most titled clubs in the country.',
            date: 'December 15, 2024',
            time: '20:00',
            location: 'Estadio Nacional, Lima',
            image: 'https://images.unsplash.com/photo-1579952363873-27f3bade9f55?w=800&h=400&fit=crop',
            sport: 'Football',
            price: '50-150 PEN',
            category: 'Match',
            capacity: '45,000',
            highlights: [
                'Historic match between the two most titled clubs',
                'Electric atmosphere at the national stadium',
                'Chance to meet football legends',
                'Musical and cultural entertainment before the match',
            ],
            rules: [
                'No dangerous objects allowed',
                'Arrival recommended 1h before the start',
                'Team colors clothing allowed',
                'Strict compliance with safety instructions',
            ],
        },
        2: {
            id: 2,
            title: 'Basketball Championship',
            description: 'National basketball championship final - The best of Peruvian basketball compete for the national crown.',
            date: 'December 20, 2024',
            time: '19:30',
            location: 'Coliseo Eduardo Dibós, Lima',
            image: 'https://images.unsplash.com/photo-1546519638-68e109498ffc?w=800&h=400&fit=crop',
            sport: 'Basketball',
            price: '30-80 PEN',
            category: 'Final',
            capacity: '8,000',
            highlights: [
                'National championship final',
                'International level of play',
                'Halftime shows and entertainment',
                'Meet the players after the match',
            ],
            rules: [
                'No smoking inside the venue',
                'Professional cameras prohibited',
                'Respect other spectators',
                'Follow staff instructions',
            ],
        },
    };

    // États du formulaire
    const [highlights, setHighlights] = useState(['']);
    const [rules, setRules] = useState(['']);
    const [sport, setSport] = useState('');
    const [categoryInput, setCategoryInput] = useState('');
    const [category, setCategory] = useState('');
    const [date, setDate] = useState(null);
    const [time, setTime] = useState(null);
    const [banner, setBanner] = useState('');
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [price, setPrice] = useState('');
    const [capacity, setCapacity] = useState('');
    const [location, setLocation] = useState('');
    const [clubId, setClubId] = useState('');

    // Prefill if editing
    useEffect(() => {
        if (id && eventData[id]) {
            const ev = eventData[id];
            setSport(ev.sport || '');
            setCategory(ev.category || '');
            setCategoryInput('');
            setTitle(ev.title || '');
            setDescription(ev.description || '');
            // Date conversion (ex: 'December 15, 2024' -> Date)
            if (ev.date) {
                const [month, day, year] = ev.date.replace(',', '').split(' ');
                const monthMap = {
                    'January': 0, 'February': 1, 'March': 2, 'April': 3, 'May': 4, 'June': 5,
                    'July': 6, 'August': 7, 'September': 8, 'October': 9, 'November': 10, 'December': 11
                };
                setDate(new Date(Number(year), monthMap[month], Number(day)));
            } else {
                setDate(null);
            }
            // Time conversion (ex: '20:00' -> Date)
            if (ev.time) {
                const [h, m] = ev.time.split(':');
                const d = new Date();
                d.setHours(Number(h));
                d.setMinutes(Number(m));
                d.setSeconds(0);
                d.setMilliseconds(0);
                setTime(d);
            } else {
                setTime(null);
            }
            setPrice(ev.price || '');
            // Capacity: remove comma and convert to number
            setCapacity(ev.capacity ? ev.capacity.replace(/,/g, '') : '');
            setLocation(ev.location || '');
            setHighlights(ev.highlights || ['']);
            setRules(ev.rules || ['']);
            setBanner(ev.image || '');
            // Prefill clubId if info available (mock: clubId=101 for football, 102 for basketball)
            if (ev.sport === 'Football') setClubId('101');
            else if (ev.sport === 'Basketball') setClubId('102');
        }
    }, [id]);

    const handleHighlightChange = (idx, value) => {
        const arr = [...highlights];
        arr[idx] = value;
        setHighlights(arr);
    };
    const addHighlight = () => {
        if (highlights.length < MAX_POINTS) setHighlights([...highlights, '']);
    };
    const removeHighlight = (idx) => {
        setHighlights(highlights.filter((_, i) => i !== idx));
    };

    const handleRuleChange = (idx, value) => {
        const arr = [...rules];
        arr[idx] = value;
        setRules(arr);
    };
    const addRule = () => {
        if (rules.length < MAX_POINTS) setRules([...rules, '']);
    };
    const removeRule = (idx) => {
        setRules(rules.filter((_, i) => i !== idx));
    };

    const handleCategoryKeyDown = (e) => {
        if (e.key === 'Enter' && categoryInput.trim() && !categoryInput.includes(' ')) {
            setCategory(categoryInput.trim());
            setCategoryInput('');
            e.preventDefault();
        }
    };
    const handleCategoryDelete = () => setCategory('');

    // Gestion upload bannière (mock)
    const handleBannerUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (ev) => setBanner(ev.target.result);
            reader.readAsDataURL(file);
        }
    };

    return (
        <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4, p: 4, background: 'white', borderRadius: 4, boxShadow: '0 4px 24px rgba(255,107,107,0.10)' }}>
            <Typography variant="h4" fontWeight={700} gutterBottom>
                Event Configuration
            </Typography>
            <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel id="sport-label">Sport</InputLabel>
                <Select
                    labelId="sport-label"
                    value={sport}
                    label="Sport"
                    onChange={e => setSport(e.target.value)}
                >
                    {sportsList.map(s => (
                        <MenuItem key={s} value={s}>{s}</MenuItem>
                    ))}
                </Select>
            </FormControl>
            <Box sx={{ mb: 3 }}>
                {!category ? (
                    <TextField
                        label="Category (one word, ex: Final, Tournament...)"
                        fullWidth
                        value={categoryInput}
                        onChange={e => setCategoryInput(e.target.value.replace(/\s/g, ''))}
                        onKeyDown={handleCategoryKeyDown}
                        helperText="Press Enter to validate. One word."
                    />
                ) : (
                    <Chip
                        label={category}
                        onDelete={handleCategoryDelete}
                        sx={{
                            fontWeight: 700,
                            fontSize: 15,
                            borderRadius: 999,
                            px: 2,
                            height: 32,
                            background: 'linear-gradient(135deg, #FF6B6B 0%, #E53E3E 100%)',
                            color: 'white',
                        }}
                    />
                )}
            </Box>
            <TextField label="Event title" fullWidth sx={{ mb: 3 }} value={title} onChange={e => setTitle(e.target.value)} />
            <TextField label="Description" fullWidth multiline rows={3} sx={{ mb: 3 }} value={description} onChange={e => setDescription(e.target.value)} />
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={frLocale}>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6}>
                        <DatePicker
                            label="Date"
                            value={date}
                            onChange={setDate}
                            slotProps={{
                                textField: { fullWidth: true, size: 'medium', sx: { background: 'white', borderRadius: 2 } }
                            }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TimePicker
                            label="Time"
                            value={time}
                            onChange={setTime}
                            slotProps={{
                                textField: { fullWidth: true, size: 'medium', sx: { background: 'white', borderRadius: 2 } }
                            }}
                        />
                    </Grid>
                </Grid>
            </LocalizationProvider>
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                    <TextField label="Price" fullWidth value={price} onChange={e => setPrice(e.target.value)} />
                </Grid>
                <Grid item xs={6}>
                    <TextField label="Capacity" type="number" fullWidth value={capacity} onChange={e => setCapacity(e.target.value)} />
                </Grid>
            </Grid>
            <TextField label="Location" fullWidth sx={{ mb: 3 }} value={location} onChange={e => setLocation(e.target.value)} />
            <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel id="club-label">Organizing club</InputLabel>
                <Select
                    labelId="club-label"
                    value={clubId}
                    label="Organizing club"
                    onChange={e => setClubId(e.target.value)}
                    required
                >
                    {userMock.clubs.map(club => (
                        <MenuItem key={club.id} value={club.id.toString()}>{club.name} ({club.city})</MenuItem>
                    ))}
                </Select>
            </FormControl>

            {/* Event highlights */}
            <Box sx={{ mb: 3 }}>
                <Typography fontWeight={600} sx={{ mb: 1 }}>Event highlights (max 4)</Typography>
                {highlights.map((pt, idx) => (
                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <TextField
                            value={pt}
                            onChange={e => handleHighlightChange(idx, e.target.value)}
                            fullWidth
                            size="small"
                            placeholder={`Highlight #${idx + 1}`}
                        />
                        {highlights.length > 1 && (
                            <IconButton onClick={() => removeHighlight(idx)} color="error" sx={{ ml: 1 }}><DeleteIcon /></IconButton>
                        )}
                    </Box>
                ))}
                <Button
                    startIcon={<AddIcon />}
                    onClick={addHighlight}
                    disabled={highlights.length >= MAX_POINTS}
                    sx={{ mt: 1, fontWeight: 600, borderRadius: 2 }}
                >
                    Add highlight
                </Button>
            </Box>

            {/* Rules and guidelines */}
            <Box sx={{ mb: 4 }}>
                <Typography fontWeight={600} sx={{ mb: 1 }}>Rules and guidelines (max 4)</Typography>
                {rules.map((pt, idx) => (
                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <TextField
                            value={pt}
                            onChange={e => handleRuleChange(idx, e.target.value)}
                            fullWidth
                            size="small"
                            placeholder={`Rule or guideline #${idx + 1}`}
                        />
                        {rules.length > 1 && (
                            <IconButton onClick={() => removeRule(idx)} color="error" sx={{ ml: 1 }}><DeleteIcon /></IconButton>
                        )}
                    </Box>
                ))}
                <Button
                    startIcon={<AddIcon />}
                    onClick={addRule}
                    disabled={rules.length >= MAX_POINTS}
                    sx={{ mt: 1, fontWeight: 600, borderRadius: 2 }}
                >
                    Add rule/guideline
                </Button>
            </Box>

            {/* Banner */}
            <Box sx={{ mb: 3 }}>
                <Typography fontWeight={600} sx={{ mb: 1 }}>Event banner</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {banner && (
                        <Box component="img" src={banner} alt="Banner" sx={{ width: 120, height: 70, objectFit: 'cover', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.10)' }} />
                    )}
                    <Button component="label" variant="outlined" color="error" startIcon={<PhotoCamera />} sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none', fontSize: 13, height: 38 }}>
                        {banner ? 'Change banner' : 'Add banner'}
                        <input type="file" accept="image/*" hidden onChange={handleBannerUpload} />
                    </Button>
                </Box>
            </Box>

            <Button variant="contained" color="primary" sx={{ fontWeight: 700, borderRadius: 3, px: 4, py: 1.5, fontSize: '1.1rem', background: 'linear-gradient(135deg, #FF6B6B 0%, #E53E3E 100%)', color: 'white', boxShadow: '0 4px 16px rgba(255,107,107,0.10)', textTransform: 'none', mb: 10 }}>
                Save
            </Button>
        </Box>
    );
};

export default AdminEventConfig; 